import type { ComponentProps } from 'react';
import { useCallback } from 'react';

import { useLabel } from '~hooks/useLabels';
import { Filter } from '~modules/media-library/model/Filter';
import { Button } from '~modules/media-library/view/Button';
import { FilterSelectToggleButton } from '~modules/media-library/view/FilterSelect/FilterSelectToggleButton';
import { toggleItemInPrimitiveArray } from '~services/immutable';

const MAX_FILTERS = 5;

type FilterSelectPanelProps = {
  labelAction: string;
  onActionClick: () => void;
  filterList: Filter[];
  activeFilterIds: Array<Filter['id']>;
  onChangeActiveFilterIds: (filterIds: Array<Filter['id']>) => void;
};

export function FilterSelectPanel({
  labelAction,
  onActionClick,
  filterList = [],
  activeFilterIds = [],
  onChangeActiveFilterIds,
}: FilterSelectPanelProps) {
  const count = activeFilterIds.length;
  const isMaxCount = count >= MAX_FILTERS;
  const msgFilterSelectCount = useLabel('msgFilterSelectCount', { count, max: MAX_FILTERS });

  const isFilterSelected = useCallback(
    (filterId: Filter['id']) => activeFilterIds.includes(filterId),
    [activeFilterIds],
  );

  // Hanlder: make callback for onClick on filter toggle buttons
  const makeHandleFilterClick = useCallback(
    (filterId: Filter['id']): ComponentProps<typeof FilterSelectToggleButton>['onClick'] =>
      evt => {
        // Stop event bubble because that would close the popover
        evt.stopPropagation();
        evt.preventDefault();

        // update list
        const updatedActiveFilterIds = toggleItemInPrimitiveArray(activeFilterIds, filterId);
        // check that we don't exeed our max limit
        if (updatedActiveFilterIds.length > MAX_FILTERS) return;
        // fire update
        onChangeActiveFilterIds(updatedActiveFilterIds);
      },
    [activeFilterIds, onChangeActiveFilterIds],
  );

  return (
    <div className="ml--filter-select--panel rounded bg-white shadow-xl flex flex-col w-full p-3 gap-3">
      <div className="flex justify-between items-center">
        <div className="text-base">{msgFilterSelectCount}</div>
        <Button variant="link" onClick={onActionClick} className="font-semibold">
          {labelAction}
        </Button>
      </div>
      <div className="flex flex-wrap gap-3">
        {filterList.map(filter => {
          const selected = isFilterSelected(filter.id);
          const disabled = !selected && isMaxCount;
          return (
            <FilterSelectToggleButton
              key={filter.id}
              onClick={makeHandleFilterClick(filter.id)}
              selected={selected}
              disabled={disabled}
            >
              {filter.label}
            </FilterSelectToggleButton>
          );
        })}
      </div>
    </div>
  );
}
